import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ArrowUpRight, CalendarDays, Loader2 } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND } from "@/data/content";
import { track } from "@/lib/analytics";
import { getAttribution } from "@/lib/utm";
import { usePageMeta } from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

const TIME_SLOTS = [
  "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM",
];

const initialForm = { name: "", email: "", phone: "", company: "", message: "" };

const SOLUTION_OPTIONS = [
  { value: "growth360", label: "Growth360 — Business Growth Diagnostic" },
  { value: "profitshield", label: "ProfitShield — Profit Leakage & Margin Recovery" },
  { value: "salesmax", label: "SalesMax — Sales Productivity & Execution" },
  { value: "channelmax", label: "ChannelMax — Distribution & Channel Performance" },
  { value: "commercemax", label: "CommerceMax — Profitable E-commerce Growth" },
  { value: "not-sure", label: "Not sure / Other growth challenge" },
];

export default function ContactPage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [dateStr, setDateStr] = useState("");
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const solutionParam = searchParams.get("solution");
  const sourceParam = searchParams.get("source");
  const [solution, setSolution] = useState(
    SOLUTION_OPTIONS.some((o) => o.value === solutionParam) ? solutionParam : ""
  );
  const solutionLabel = SOLUTION_OPTIONS.find((o) => o.value === solution)?.label || null;

  usePageMeta({
    title: "Contact | BLEZZINGS Growth Partners",
    description:
      "Discuss your growth challenge with BLEZZINGS Growth Partners. Tell us about your business and the commercial challenge you want to address.",
    canonical: "https://blezzings.co/contact",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Contact", url: "https://blezzings.co/contact" },
    ],
  });

  const todayIso = new Date().toISOString().slice(0, 10);
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    const attr = getAttribution();
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        message: form.message.trim(),
        preferred_date: dateStr ? format(parseISO(dateStr), "EEE, dd MMM yyyy") : null,
        preferred_time: time || null,
        service: "General enquiry",
        solution: solution || null,
        source_page: "/contact",
        source: sourceParam || null,
        referrer: attr.referrer || null,
        utm_source: attr.utm_source || null,
        utm_medium: attr.utm_medium || null,
        utm_campaign: attr.utm_campaign || null,
        utm_content: attr.utm_content || null,
        utm_term: attr.utm_term || null,
      });
      track("generate_lead", { service: "General enquiry", source_page: "/contact", form: "contact_page" });
      track("consultation_request", { form_source: "contact-page", solution_id: solution || null, source: sourceParam || null, has_date: !!dateStr, has_time: !!time });
      track("lead_submit", { solution_id: solution || null, source_page: sourceParam || "/contact" });
      toast.success("Thanks — we'll be in touch shortly.", {
        description: "Your enquiry has been received. We will get back within one business day.",
      });
      setForm(initialForm);
      setDateStr("");
      setTime("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative">
      <Nav onBook={() => setBookOpen(true)} />

      {/* Header */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-[#fafafa] overflow-hidden">
        <div className={`${GRID} grid grid-cols-12 gap-10 items-center`}>
          <div className="col-span-12 lg:col-span-7">
            <SectionBadge label="Contact" />
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-3xl">
              Discuss Your Growth Challenge.
            </h1>
            <div className="mt-6 max-w-2xl space-y-5 text-zinc-700 leading-relaxed">
              <p>
                Tell us about your business and the commercial challenge you want
                to address.
              </p>
              <p>
                Whether you need to accelerate revenue, improve profitability,
                strengthen distribution, enter new markets, build your brand or
                improve digital growth, we would be interested in understanding
                your priorities.
              </p>
              <p className="text-black font-medium">
                We will review your enquiry and explore whether there is a
                suitable fit.
              </p>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 reveal">
            <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
              <img
                src="/photos/contact-hero.jpg"
                alt="A first consultation — a BLEZZINGS partner listening to a client's growth challenge"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Form + details */}
      <section data-testid="contact-form-section" className="py-16 md:py-20 bg-white">
        <div className={`${GRID} grid grid-cols-12 gap-10 md:gap-14`}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="col-span-12 lg:col-span-7 space-y-5"
          >
            {solutionLabel && solutionParam && (
              <div
                data-testid="contact-solution-notice"
                className="border border-black/15 bg-[#fafafa] px-4 py-3 text-sm text-zinc-700"
              >
                Enquiring about: <span className="font-medium text-black">{solutionLabel}</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name*" htmlFor="ct-name">
                <Input id="ct-name" data-testid="ct-name" value={form.name} onChange={setField("name")} required placeholder="Full name" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="Email*" htmlFor="ct-email">
                <Input id="ct-email" data-testid="ct-email" type="email" value={form.email} onChange={setField("email")} required placeholder="you@company.com" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="Phone" htmlFor="ct-phone">
                <Input id="ct-phone" data-testid="ct-phone" value={form.phone} onChange={setField("phone")} placeholder="+91 ..." className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="Company" htmlFor="ct-company">
                <Input id="ct-company" data-testid="ct-company" value={form.company} onChange={setField("company")} placeholder="Company / role" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="Solution of interest" htmlFor="ct-solution">
                <Select value={solution} onValueChange={setSolution}>
                  <SelectTrigger id="ct-solution" data-testid="ct-solution-trigger" className="rounded-none border-zinc-300 h-11 focus:ring-0 focus:border-black">
                    <SelectValue placeholder="Select a solution (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {SOLUTION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`ct-solution-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Preferred date" htmlFor="ct-date">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      id="ct-date"
                      data-testid="ct-date"
                      className="flex h-11 w-full items-center justify-between border border-zinc-300 bg-transparent px-3 text-left text-sm transition-colors hover:border-black"
                    >
                      <span className={dateStr ? "text-black" : "text-zinc-400"}>
                        {dateStr ? format(parseISO(dateStr), "EEE, dd MMM yyyy") : "Pick a preferred date"}
                      </span>
                      <CalendarDays className="h-4 w-4 text-zinc-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-none border-zinc-300" align="start">
                    <Calendar
                      mode="single"
                      selected={dateStr ? parseISO(dateStr) : undefined}
                      onSelect={(d) => d && setDateStr(format(d, "yyyy-MM-dd"))}
                      disabled={{ before: new Date(todayIso) }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </Field>
              <Field label="Preferred time" htmlFor="ct-time">
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger id="ct-time" data-testid="ct-time-trigger" className="rounded-none border-zinc-300 h-11 focus:ring-0 focus:border-black">
                    <SelectValue placeholder="Pick a time slot" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot} value={slot} data-testid={`ct-time-${slot}`}>
                        {slot} IST
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Your challenge or opportunity*" htmlFor="ct-message">
              <Textarea id="ct-message" data-testid="ct-message" value={form.message} onChange={setField("message")} required rows={5} placeholder="Briefly describe the challenge you're solving for or the opportunity you're chasing — and the outcome you'd like." className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black resize-none" />
            </Field>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-zinc-500 max-w-xs leading-relaxed">
                Your information will be used to respond to your enquiry, in
                accordance with our{" "}
                <Link to="/privacy-policy" data-testid="ct-privacy-link" className="underline underline-offset-2 hover:text-zinc-800 transition-colors">
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link to="/terms-of-use" data-testid="ct-terms-link" className="underline underline-offset-2 hover:text-zinc-800 transition-colors">
                  Terms of Use
                </Link>
                .
              </p>
              <button
                data-testid="ct-submit"
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center justify-center gap-2 h-12 px-7 bg-black text-white text-[15px] font-medium hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Sending</>
                ) : (
                  <>
                    Submit Your Growth Challenge
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Contact details */}
          <div className="col-span-12 lg:col-span-5">
            <div className="bg-[#F2F2F2] border border-black/5 p-8 md:p-10">
              <div className="-mx-8 -mt-8 md:-mx-10 md:-mt-10 mb-8 aspect-[16/9] overflow-hidden bg-zinc-200">
                <img
                  src="/photos/contact-direct.jpg"
                  alt="A BLEZZINGS partner responding to an enquiry — phone, laptop and calendar at hand"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500">
                Direct Contact
              </div>
              <ul className="mt-6 space-y-4 text-[15px]">
                <li>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Location</div>
                  <div className="mt-1 text-black">Whitefield, Bengaluru</div>
                </li>
                <li>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Phone</div>
                  <a href={BRAND.contact.phoneHref} onClick={() => track("contact_phone_click", { source_page: window.location.pathname })} className="mt-1 inline-block text-black hover:underline underline-offset-4">
                    {BRAND.contact.phone}
                  </a>
                </li>
                <li>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</div>
                  <a href={`mailto:${BRAND.contact.email}`} onClick={() => track("contact_email_click", { source_page: window.location.pathname })} className="mt-1 inline-block text-black hover:underline underline-offset-4">
                    {BRAND.contact.email}
                  </a>
                </li>
                <li>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">LinkedIn</div>
                  <a href={BRAND.contact.linkedinCompany} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-black hover:underline underline-offset-4">
                    BLEZZINGS Growth Partners
                  </a>
                </li>
              </ul>
              <p className="mt-8 pt-6 border-t border-black/10 text-sm text-zinc-600 leading-relaxed">
                We reply within one business day.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => setBookOpen(true)} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
